import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast, Toaster } from "sonner";
import { ArrowLeft, Send, Paperclip, Mic, StopCircle, Image as ImageIcon, Video as VideoIcon, X, MoreVertical, CheckCheck, Check, Trash2 } from "lucide-react";

const BG_PATTERN = "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='60' height='60' viewBox='0 0 60 60'><g fill='%23ffffff' fill-opacity='0.05'><circle cx='10' cy='10' r='1'/><circle cx='30' cy='30' r='1'/><circle cx='50' cy='50' r='1'/></g></svg>";

export default function Chat() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordSecs, setRecordSecs] = useState(0);
  const scrollerRef = useRef(null);
  const fileImgRef = useRef(null);
  const fileVidRef = useRef(null);
  const mediaRecRef = useRef(null);
  const chunksRef = useRef([]);
  const recTimerRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/chat/messages");
      setMessages(data.messages || []);
      await api.post("/chat/mark-read").catch(() => {});
    } catch {}
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [messages]);

  const send = async (payload) => {
    if (sending) return;
    setSending(true);
    try {
      const { data } = await api.post("/chat/messages", payload);
      setMessages(m => [...m, data]);
      setText("");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  const sendText = () => {
    const t = text.trim();
    if (!t) return;
    send({ text: t });
  };

  const readAsDataURL = (file) => new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });

  const uploadFile = async (file, file_type) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) return toast.error("Max 25MB");
    setAttachOpen(false);
    setSending(true);
    try {
      const dataUrl = await readAsDataURL(file);
      const { data } = await api.post("/chat/messages", { file_data_url: dataUrl, filename: file.name, file_type });
      setMessages(m => [...m, data]);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => chunksRef.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        const file = new File([blob], `voice-${Date.now()}.webm`, { type: "audio/webm" });
        await uploadFile(file, "audio");
      };
      rec.start();
      mediaRecRef.current = rec;
      setRecording(true);
      setRecordSecs(0);
      recTimerRef.current = setInterval(() => setRecordSecs(s => s + 1), 1000);
    } catch (e) { toast.error("Mic access denied"); }
  };

  const stopRecording = () => {
    if (mediaRecRef.current) mediaRecRef.current.stop();
    clearInterval(recTimerRef.current);
    setRecording(false);
    setRecordSecs(0);
  };

  const cancelRecording = () => {
    if (mediaRecRef.current) {
      mediaRecRef.current.ondataavailable = null;
      mediaRecRef.current.onstop = null;
      mediaRecRef.current.stop();
    }
    clearInterval(recTimerRef.current);
    setRecording(false);
    setRecordSecs(0);
    chunksRef.current = [];
  };

  const secToMMSS = (s) => `${Math.floor(s / 60).toString().padStart(2, "0")}:${(s % 60).toString().padStart(2, "0")}`;

  return (
    <div className="fixed inset-0 z-40 flex flex-col bg-[#0b141a]" data-testid="user-chat-page">
      <Toaster richColors position="top-center" />
      {/* Header — WhatsApp green */}
      <div className="shrink-0 bg-[#008069] text-white flex items-center gap-3 px-3 py-3 shadow-md">
        <button onClick={() => navigate(-1)} className="p-1 rounded-full hover:bg-white/10" data-testid="chat-back">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-lg font-bold shrink-0 overflow-hidden">
          <img src="/brand/reddy-club-logo.png" alt="RC" className="w-full h-full object-cover" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-base leading-tight truncate">Reddy Club Support</div>
          <div className="text-[11px] text-white/80">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-300 mr-1 align-middle animate-pulse" />
            Online · Fast reply
          </div>
        </div>
        <button
          data-testid="chat-clear-btn"
          onClick={async () => {
            if (!window.confirm("Puri chat delete karni hai? Yeh sirf aapke phone se hatega.")) return;
            try {
              await api.delete("/chat/messages");
              setMessages([]);
              toast.success("Chat cleared");
            } catch (e) { toast.error(formatApiError(e)); }
          }}
          className="p-2 rounded-full hover:bg-white/10 active:bg-white/20"
          title="Clear chat"
        >
          <Trash2 className="w-5 h-5" />
        </button>
      </div>

      {/* Messages */}
      <div
        ref={scrollerRef}
        className="flex-1 overflow-y-auto px-3 py-3 space-y-1"
        style={{
          backgroundColor: "#0b141a",
          backgroundImage: `url('${BG_PATTERN}')`,
          backgroundRepeat: "repeat",
        }}
        data-testid="chat-messages"
      >
        {messages.length === 0 && (
          <div className="text-center text-white/60 text-xs my-8">
            <div className="inline-block bg-[#182229] rounded-full px-4 py-2">
              👋 Namaste! Koi bhi query ho, yahan message karo — admin turant reply karega.
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <ChatBubble key={m.id} msg={m} prev={messages[i - 1]} />
        ))}
      </div>

      {/* Recording UI or Input */}
      {recording ? (
        <div className="shrink-0 bg-[#202c33] p-3 flex items-center gap-3" data-testid="recording-bar">
          <button onClick={cancelRecording} className="text-rose-400 p-2 rounded-full hover:bg-rose-500/20"><X className="w-5 h-5" /></button>
          <div className="flex-1 flex items-center gap-2 text-white">
            <span className="w-2.5 h-2.5 rounded-full bg-rose-500 animate-pulse" />
            <span className="text-sm font-mono">{secToMMSS(recordSecs)}</span>
            <span className="text-xs text-white/60">Recording…</span>
          </div>
          <button onClick={stopRecording} data-testid="stop-record" className="w-11 h-11 rounded-full bg-[#00a884] text-white flex items-center justify-center shadow-lg">
            <Send className="w-5 h-5" />
          </button>
        </div>
      ) : (
        <div className="shrink-0 bg-[#202c33] p-2 flex items-end gap-2">
          <div className="relative">
            <button onClick={() => setAttachOpen(v => !v)} className="w-11 h-11 rounded-full text-[#aebac1] hover:bg-white/10 flex items-center justify-center" data-testid="attach-btn">
              <Paperclip className="w-5 h-5" />
            </button>
            {attachOpen && (
              <div className="absolute bottom-14 left-0 bg-[#233138] rounded-xl shadow-2xl p-2 flex flex-col gap-1 w-44" data-testid="attach-menu">
                <button onClick={() => { fileImgRef.current?.click(); setAttachOpen(false); }} className="flex items-center gap-3 px-3 py-2 rounded-lg text-white hover:bg-white/10 text-sm">
                  <div className="w-9 h-9 rounded-full bg-[#a04dbb] flex items-center justify-center"><ImageIcon className="w-4 h-4" /></div>
                  Image
                </button>
                <button onClick={() => { fileVidRef.current?.click(); setAttachOpen(false); }} className="flex items-center gap-3 px-3 py-2 rounded-lg text-white hover:bg-white/10 text-sm">
                  <div className="w-9 h-9 rounded-full bg-[#d3396d] flex items-center justify-center"><VideoIcon className="w-4 h-4" /></div>
                  Video
                </button>
              </div>
            )}
          </div>
          <input ref={fileImgRef} type="file" accept="image/*" className="hidden" onChange={e => uploadFile(e.target.files?.[0], "image")} data-testid="file-image" />
          <input ref={fileVidRef} type="file" accept="video/*" className="hidden" onChange={e => uploadFile(e.target.files?.[0], "video")} data-testid="file-video" />

          <div className="flex-1 bg-[#2a3942] rounded-3xl flex items-center px-4 min-h-[44px]">
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendText(); } }}
              placeholder="Message"
              rows={1}
              className="flex-1 bg-transparent text-white placeholder-[#8696a0] resize-none outline-none py-2 max-h-32"
              data-testid="chat-text-input"
            />
          </div>

          {text.trim() ? (
            <button onClick={sendText} disabled={sending} data-testid="chat-send-btn"
              className="w-11 h-11 rounded-full bg-[#00a884] hover:bg-[#00c9a0] text-white flex items-center justify-center shadow-lg transition disabled:opacity-60">
              <Send className="w-5 h-5" />
            </button>
          ) : (
            <button onClick={startRecording} data-testid="mic-btn"
              className="w-11 h-11 rounded-full bg-[#00a884] hover:bg-[#00c9a0] text-white flex items-center justify-center shadow-lg transition">
              <Mic className="w-5 h-5" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function ChatBubble({ msg, prev }) {
  const isUser = msg.sender === "user";
  const t = new Date(msg.created_at);
  const timeLabel = t.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const showDateSep = !prev || new Date(prev.created_at).toDateString() !== t.toDateString();

  return (
    <>
      {showDateSep && (
        <div className="flex justify-center my-2">
          <div className="bg-[#182229] text-[#8696a0] text-[11px] px-3 py-1 rounded-md shadow">
            {t.toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" })}
          </div>
        </div>
      )}
      <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
        <div
          className={`relative max-w-[75%] px-2.5 py-1.5 rounded-lg text-sm shadow ${
            isUser ? "bg-[#005c4b] text-white" : "bg-[#202c33] text-white"
          }`}
          data-testid={`msg-${msg.id}`}
        >
          {msg.file_url && (
            <div className="mb-1 -mx-1.5 -mt-1">
              <ChatAttachment msg={msg} />
            </div>
          )}
          {msg.text && <div className="whitespace-pre-wrap break-words pr-14 leading-snug">{msg.text}</div>}
          <div className="absolute bottom-1 right-2 flex items-center gap-1 text-[10px] text-white/60">
            <span>{timeLabel}</span>
            {isUser && (msg.read_by_admin ? <CheckCheck className="w-3.5 h-3.5 text-sky-300" /> : <Check className="w-3.5 h-3.5" />)}
          </div>
          <div className="h-4" />
        </div>
      </div>
    </>
  );
}

function ChatAttachment({ msg }) {
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const url = msg.file_url?.startsWith("http") ? msg.file_url : `${backend}${msg.file_url}`;
  if (msg.file_type === "image") {
    return <img src={url} alt="attachment" className="max-w-full rounded-md" style={{ maxHeight: 300 }} />;
  }
  if (msg.file_type === "video") {
    return <video src={url} controls playsInline className="max-w-full rounded-md" style={{ maxHeight: 300 }} />;
  }
  if (msg.file_type === "audio") {
    return <audio src={url} controls className="w-64 max-w-full" />;
  }
  return <a href={url} target="_blank" rel="noreferrer" className="underline">Open file</a>;
}
